import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Descriptions
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
  EyeOutlined,
  KeyOutlined
} from '@ant-design/icons';
import axios from 'axios';

// Create axios instance with JWT authentication (for admin)
const adminApi = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const { Option } = Select;

const Users = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    role: null,
    status: null
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchUsers();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize
      };

      if (filters.role) params.role = filters.role;
      if (filters.status) params.status = filters.status;

      const response = await adminApi.get('/users', { params });
      setUsers(response.data.users);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));
    } catch (error) {
      message.error('Gagal memuat data pengguna');
      console.error('Fetch users error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingUser(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.delete(`/users/${id}`);
      message.success('Pengguna berhasil dihapus');
      fetchUsers();
    } catch (error) {
      message.error('Gagal menghapus pengguna');
      console.error('Delete user error:', error);
    }
  };

  const handleView = async (id) => {
    try {
      const response = await adminApi.get(`/users/${id}`);
      setSelectedUser(response.data);
      setDetailModalVisible(true);
    } catch (error) {
      message.error('Gagal memuat detail pengguna');
      console.error('Fetch user detail error:', error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingUser) {
        await adminApi.put(`/users/${editingUser.id}`, values);
        message.success('Pengguna berhasil diperbarui');
      } else {
        await adminApi.post('/users', values);
        message.success('Pengguna berhasil ditambahkan');
      }
      setModalVisible(false);
      fetchUsers();
    } catch (error) {
      message.error(editingUser ? 'Gagal memperbarui pengguna' : 'Gagal menambahkan pengguna');
      console.error('Submit user error:', error);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'red';
      case 'merchant':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getRoleText = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'merchant':
        return 'Merchant';
      case 'user':
        return 'User';
      default:
        return role;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'orange';
      case 'suspended':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'inactive':
        return 'Tidak Aktif';
      case 'suspended':
        return 'Ditangguhkan';
      default:
        return status;
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      width: 150,
    },
    {
      title: 'Nama Lengkap',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 200,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 250,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role) => (
        <Tag color={getRoleColor(role)}>
          {getRoleText(role)}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Tanggal Dibuat',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => new Date(date).toLocaleDateString('id-ID'),
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record.id)}
          >
            Detail
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Apakah Anda yakin ingin menghapus pengguna ini?"
            onConfirm={() => handleDelete(record.id)}
            okText="Ya"
            cancelText="Tidak"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Hapus
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1>Manajemen Pengguna</h1>
        <p style={{ color: '#666' }}>Kelola semua pengguna sistem</p>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Pengguna"
              value={pagination.total}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Administrator"
              value={users.filter(u => u.role === 'admin').length}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Merchant"
              value={users.filter(u => u.role === 'merchant').length}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="User"
              value={users.filter(u => u.role === 'user').length}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filter Role"
              value={filters.role}
              onChange={(value) => setFilters(prev => ({ ...prev, role: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="admin">Administrator</Option>
              <Option value="merchant">Merchant</Option>
              <Option value="user">User</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filter Status"
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="active">Aktif</Option>
              <Option value="inactive">Tidak Aktif</Option>
              <Option value="suspended">Ditangguhkan</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <h3>Daftar Pengguna</h3>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Tambah Pengguna
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchUsers}
            >
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} pengguna`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingUser ? 'Edit Pengguna' : 'Tambah Pengguna'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {!editingUser && (
            <>
              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: 'Password wajib diisi' }]}
              >
                <Input.Password placeholder="Masukkan password" />
              </Form.Item>
            </>
          )}

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Username"
                name="username"
                rules={[{ required: true, message: 'Username wajib diisi' }]}
              >
                <Input placeholder="Masukkan username" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Email wajib diisi' },
                  { type: 'email', message: 'Format email tidak valid' }
                ]}
              >
                <Input placeholder="email@example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Nama Lengkap"
            name="full_name"
            rules={[{ required: true, message: 'Nama lengkap wajib diisi' }]}
          >
            <Input placeholder="Masukkan nama lengkap" />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Role"
                name="role"
                rules={[{ required: true, message: 'Role wajib dipilih' }]}
              >
                <Select placeholder="Pilih role">
                  <Option value="admin">Administrator</Option>
                  <Option value="merchant">Merchant</Option>
                  <Option value="user">User</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: 'Status wajib dipilih' }]}
              >
                <Select placeholder="Pilih status">
                  <Option value="active">Aktif</Option>
                  <Option value="inactive">Tidak Aktif</Option>
                  <Option value="suspended">Ditangguhkan</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Batal
              </Button>
              <Button type="primary" htmlType="submit">
                {editingUser ? 'Update' : 'Simpan'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title="Detail Pengguna"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedUser && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="ID">{selectedUser.user.id}</Descriptions.Item>
              <Descriptions.Item label="Username">{selectedUser.user.username}</Descriptions.Item>
              <Descriptions.Item label="Email">{selectedUser.user.email}</Descriptions.Item>
              <Descriptions.Item label="Nama Lengkap">{selectedUser.user.full_name}</Descriptions.Item>
              <Descriptions.Item label="Role">
                <Tag color={getRoleColor(selectedUser.user.role)}>
                  {getRoleText(selectedUser.user.role)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(selectedUser.user.status)}>
                  {getStatusText(selectedUser.user.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Tanggal Dibuat">
                {new Date(selectedUser.user.created_at).toLocaleString('id-ID')}
              </Descriptions.Item>
              <Descriptions.Item label="Terakhir Update">
                {new Date(selectedUser.user.updated_at).toLocaleString('id-ID')}
              </Descriptions.Item>
            </Descriptions>

            {selectedUser.merchantSettings && (
              <div style={{ marginTop: '24px' }}>
                <h4>
                  <KeyOutlined /> Pengaturan Merchant
                </h4>
                <Descriptions bordered column={1} style={{ marginTop: '16px' }}>
                  <Descriptions.Item label="API Key">
                    <code>{selectedUser.merchantSettings.api_key}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label="Secret Key">
                    <code>{selectedUser.merchantSettings.secret_key}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label="Webhook URL">
                    {selectedUser.merchantSettings.webhook_url || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Settlement Schedule">
                    {selectedUser.merchantSettings.settlement_schedule}
                  </Descriptions.Item>
                  <Descriptions.Item label="Auto Settlement">
                    {selectedUser.merchantSettings.auto_settlement ? 'Ya' : 'Tidak'}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Users;